import React from "react";
import type { IncludedProductsBlock } from "@/types/product";
import { useProduct } from "@/hooks/useProduct";
import { useLocalizedTexts, t } from "@/utils/translations";
import { splitOnFirstBreak } from "@/utils/text";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import { fixedCmsImage } from "@/utils/cmsImage";
import shared from "../ProductDetailContent.module.css";
import styles from "./IncludedProductsSection.module.css";

export const IncludedProductsSection: React.FC<{
  block: IncludedProductsBlock;
}> = ({ block }) => {
  const localizedTexts = useLocalizedTexts();
  const { productData } = useProduct();
  const items = productData.includedProducts ?? [];
  if (!items.length) return null;

  const heading =
    block.heading?.trim() ||
    t(localizedTexts, "Tracker.Products.WhatsInTheBox", "What's in the box");
  const subheading =
    block.subheading?.trim() ||
    t(localizedTexts, "Tracker.Products.WhatsInTheBoxSubheading", "");
  const cardCount = Math.min(items.length, 4);

  return (
    <section className={`${shared.section} ${shared.bgFull}`}>
      <div className={shared.inner}>
        <h2 className={styles.heading}>{heading}</h2>
        {subheading && <p className={styles.subheading}>{subheading}</p>}
        <div
          className={styles.grid}
          style={{ ["--inbox-count" as string]: cardCount }}
        >
          {items.slice(0, 4).map((item) => {
            const desc = item.compositions?.productContent?.description ?? "";
            const { first } = splitOnFirstBreak(desc);
            return (
              <div key={item.id} className={styles.card}>
                <div className={styles.shot}>
                  {item.image?.url && (
                    <img
                      // The 140px-high shot; the width follows the image.
                      {...fixedCmsImage(item.image.url, { height: 140 })}
                      alt={item.name}
                      loading="lazy"
                    />
                  )}
                </div>
                {item.name && <h4 className={styles.title}>{item.name}</h4>}
                {first && (
                  <p
                    className={styles.body}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(first) }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default IncludedProductsSection;
